/**
 * Attachment upload API — multipart form data upload to /api/v1/uploads/.
 */

import { ensureCsrf, resolveApiUrl } from "./client";
import { CLIENT_PROTOCOL_HEADERS } from "./client-protocol";
import { isTauri } from "@/lib/is-tauri";
import { uploadWithProgress } from "./upload-with-progress";
import {
  sessionLatencyEnabled,
  startLatencySpan,
} from "@/lib/session-latency-trace";
import { translate } from "@/i18n";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";
import { isLocalImageType, saveLocalImage } from "./local-images";
import { canonicalAttachmentUrl } from "@/lib/attachment-media";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { useScreenshotSettings } from "@/desktop/screenshot-settings";

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1]! : null;
}

export interface UploadedImage {
  id: string;
  url: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  width: number;
  height: number;
}

export async function uploadImage(
  file: File,
  onProgress?: (percent: number) => void,
  options?: { purpose: "share" | "sync" },
): Promise<UploadedImage> {
  const extension = file.name.split(".").pop()?.toLowerCase();
  const imageType = (
    {
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      bmp: "image/bmp",
    } as Record<string, string>
  )[extension ?? ""];
  if (
    imageType &&
    !isLocalImageType(file.type) &&
    !file.type.startsWith("image/")
  )
    file = new File([file], file.name, { type: imageType });
  if (file.type.startsWith("image/") && !isLocalImageType(file.type))
    throw new Error(
      "Use a PNG, JPEG, GIF, or WebP image. This format stays off the server.",
    );
  if (isLocalImageType(file.type)) {
    const syncChosenAtStart = useScreenshotSettings.getState().syncScreenshots;
    const guard = captureDesktopChatAuthScopeGuard();
    const local = await saveLocalImage(file);
    local.url = canonicalAttachmentUrl(local.url);
    if (!guard()) throw new Error("The signed-in account changed.");
    if (
      options ||
      (syncChosenAtStart && useScreenshotSettings.getState().syncScreenshots)
    ) {
      return uploadCloudFile(
        file,
        onProgress,
        {
          id: local.id,
          purpose: options?.purpose ?? "sync",
        },
        guard,
      );
    }
    onProgress?.(100);
    return local;
  }
  return uploadCloudFile(file, onProgress);
}

/** An explicit per-asset action preserves its existing chat and canvas ID. */
export function syncImageAsset(
  id: string,
  file: File,
  guard = captureDesktopChatAuthScopeGuard(),
): Promise<UploadedImage> {
  if (!guard())
    return Promise.reject(new Error("The signed-in account changed."));
  return uploadCloudFile(file, undefined, { id, purpose: "sync" }, guard);
}

async function uploadCloudFile(
  file: File,
  onProgress?: (percent: number) => void,
  sync?: { id: string; purpose: "share" | "sync" },
  guard: () => boolean = () => true,
): Promise<UploadedImage> {
  const operationId = sessionLatencyEnabled() ? crypto.randomUUID() : undefined;
  const finishUpload = startLatencySpan("image_upload.total", {
    operationId,
    bytes: file.size,
  });
  let uploaded: UploadedImage | undefined;
  let outcome = "error";
  try {
    const finishAuth = startLatencySpan("image_upload.auth", { operationId });
    const headers: Record<string, string> = { ...CLIENT_PROTOCOL_HEADERS };
    let authOutcome = "error";
    let tokenLookupFailed = false;
    try {
      await ensureCsrf();
      // Desktop bundled .app uses Bearer token auth (no Django session
      // cookie / CSRF). Web + tauri-dev use the CSRF cookie.
      if (isTauri()) {
        const { invoke } = await import("@tauri-apps/api/core");
        const token = await invoke<string | null>("get_auth_token").catch(
          () => {
            tokenLookupFailed = true;
            return null;
          },
        );
        if (token) headers["Authorization"] = `Bearer ${token}`;
      } else {
        const csrfToken = getCsrfToken();
        if (csrfToken) headers["X-CSRFToken"] = csrfToken;
      }
      authOutcome = tokenLookupFailed ? "error" : "success";
    } finally {
      finishAuth({ outcome: authOutcome });
    }

    if (!guard()) throw new Error("The signed-in account changed.");
    const formData = new FormData();
    formData.append("file", file);
    if (sync)
      formData.append("purpose", sync.purpose === "share" ? "sharing" : "sync");
    const endpoint = sync
      ? `/api/v1/uploads/${sync.id}/sync/`
      : "/api/v1/uploads/";
    const finishHttp = startLatencySpan("image_upload.http", { operationId });
    let httpOutcome = "error";
    try {
      if (onProgress) {
        uploaded = await uploadWithProgress(
          resolveApiUrl(endpoint),
          headers,
          formData,
          onProgress,
        );
        httpOutcome = "success";
      } else {
        const response = await fetch(resolveApiUrl(endpoint), {
          method: "POST",
          headers,
          credentials: "include",
          body: formData,
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({ error: "" }));
          throw new Error(
            data.error ||
              translate(libErrorsMessages, "uploadFailedStatus", {
                status: response.status,
              }),
          );
        }
        const json = await response.json();
        uploaded = json.data as UploadedImage;
        httpOutcome = "success";
      }
    } finally {
      finishHttp({ outcome: httpOutcome });
    }
    if (!guard()) throw new Error("The signed-in account changed.");
    if (uploaded.url) uploaded.url = canonicalAttachmentUrl(uploaded.url);
    outcome = "success";
    return uploaded;
  } finally {
    finishUpload({ outcome, ...(uploaded ? { imageId: uploaded.id } : {}) });
  }
}
